import axios from 'axios'
import { camelCase } from 'lodash/string'
import { getCsrfToken, getCsrfCookie } from './security'
import { forceLogout } from '../../auth/forceLogout'

const instance = axios.create({
  baseURL: import.meta.env.VITE_API,
  timeout: 180000,
  withCredentials: true,
})

instance.interceptors.request.use(
  // Do something before request is sent
  (config) => {
    return protectByCsrf(config).then((protectedConfig) => protectedConfig);
  },
  // Do something with request error
  (error) => Promise.reject(error),
)

async function protectByCsrf(config) {
  if (!config.csrf) return config;
  let csrfToken = getCsrfCookie()

  if (!csrfToken) {
    await getCsrfToken()
    csrfToken = getCsrfCookie()
  }

  if (csrfToken) {
    config.headers = {
      ...config.headers,
      'X-CSRFToken': csrfToken,
    };
  }
  return config;
}

/**
 * The backend returns 403 both for "you may not do this" and for "your session no
 * longer resolves to a company membership". Only the latter means the session is dead,
 * so only that one force-logs-out; every other 403 stays a normal error for the calling
 * screen to handle.
 *
 * Mirrors SESSION_COMPANY_MISMATCH in api/utils/validator.py; keep the two in sync. The
 * code sits at the top level on the error-handler path and under `data` on the
 * controller-serialized one.
 */
const SESSION_COMPANY_MISMATCH = 'session_company_mismatch'

function isDeadSessionForbidden(status, error) {
  if (status !== 403) return false
  const body = error.response?.data || {}
  return (body.applicationCode ?? body.data?.applicationCode) === SESSION_COMPANY_MISMATCH
}

/**
 * function that returns the object recursively with camel case keys
 * @param {Object} data
 * @returns {Object} object with camel case keys
 */
function toCamelCase(data) {
  if (Array.isArray(data)) {
    return data.map((value) => {
      if (value instanceof Object) return toCamelCase(value)
      return value
    })
  }
  if (data instanceof Object) {
    const convertedDate = {}
    Object.entries(data).forEach(([key, value]) => {
      convertedDate[camelCase(key)] = toCamelCase(value)
    })
    return convertedDate
  }
  // base case
  return data
}

// Add a response interceptor
instance.interceptors.response.use(
  (response) => {
    // Skip transformation for blob responses (file downloads)
    if (response.config.responseType === 'blob' || response.data instanceof Blob) {
      return response
    }

    if (response.results) {
      return toCamelCase(response.results)
    }
    if (response.data) {
      return toCamelCase(response.data)
    }
    return toCamelCase(response)
  },
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  // Do something with response error
  (error) => {
    const msg = error.response?.data?.description || error.response?.data?.message || ''
    if (msg.includes('out of available') || msg.includes('Upgrade the subscription')) {
      window.dispatchEvent(new CustomEvent('subscription:exhausted'))
      const suppressed = new Error()
      suppressed.response = { data: { description: '__SUPPRESS__' } }
      return Promise.reject(suppressed)
    }
    // A 401 on any authed request means the v2 session is gone/expired (e.g. a
    // stale pre-v2 session). Force re-login. Exclude the login request itself so
    // wrong-credentials shows an inline error instead of redirecting.
    const url = error.config?.url || ''
    const status = error.response?.status
    if ((status === 401 || isDeadSessionForbidden(status, error)) && !url.includes('auth/login')) {
      forceLogout()
    }
    return Promise.reject(error)
  },
)

/** @param {string} resource */
/** @param {object} [config] */
const getAll = (resource, config) => {
  if (config) {
    return instance.get(resource, { ...config })
  }
  return instance.get(resource)
}

/** @param {string} resource */
/** @param {string} id */
/** @param {object} [config] */
const getSingle = (resource, id, config) => {
  if (config) {
    return instance.get(`${resource}/${id}`, { ...config })
  }
  return instance.get(`${resource}/${id}`)
}

/** @param {string} resource */
/** @param {object} model */
/** @param {object} [config] */
const post = (resource, model, config) => {
  if (config) {
    return instance.post(resource, model, { ...config })
  }
  return instance.post(resource, model)
}

/** @param {string} resource */
/** @param {string|number|null|undefined} id */
/** @param {object} model */
/** @param {object} [config] */
const put = (resource, id, model, config) => {
  const url =
    id === null || id === undefined || id === '' ? resource : `${resource}/${id}`
  if (config) {
    return instance.put(url, model, { ...config })
  }
  return instance.put(url, model)
}

/** @param {string} resource */
/** @param {string} id */
/** @param {object} model */
/** @param {object} [config] */
const patch = (resource, id, model, config) => {
  if (config) {
    return instance.patch(`${resource}/${id}`, model, { ...config })
  }
  return instance.patch(`${resource}/${id}`, model)
}

/** @param {string} resource */
/** @param {string} id */
/** @param {object} [config] */
const remove = (resource, id, config) => {
  if (config) {
    return instance.delete(`${resource}/${id}`, { ...config })
  }
  return instance.delete(`${resource}/${id}`)
}

const apiProvider = {
  instance,
  getAll,
  getSingle,
  post,
  put,
  patch,
  remove,
}

export default apiProvider
